import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';

/**
 * Track whether the component is still mounted, for guarding `setState` calls
 * after async work resolves. Shared across the modal subtree so the pattern
 * has one home.
 *
 * Usage:
 * ```
 * const mountedRef = useMountedRef();
 * await someAsync();
 * if (mountedRef.current) setX(value);
 * ```
 */
export function useMountedRef(): MutableRefObject<boolean> {
    const ref = useRef(true);
    useEffect(() => {
        // StrictMode-safe: dev-mode mount→cleanup→remount cycles must restore
        // `ref.current = true` here, since the cleanup flips it false.
        ref.current = true;
        return () => {
            ref.current = false;
        };
    }, []);
    return ref;
}
